import { type TypedDocumentNode, gql } from '@apollo/client';
import { PropsWithChildren, ReactNode } from 'react';
import styled, { CSSProperties, keyframes } from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS } from '@sorare/core/src/atoms/typography';
import { Color } from '@sorare/core/src/style/types';

import type { CardLayout_so5lineup } from './__generated__/index.graphql';

export const DELAY_BEFORE_ANIMATION_MS = 3100;

const cardEnter = keyframes`
  from {
    opacity: 0.2;
    transform: translateX(-10%) translateY(20%) rotateY(-70deg);
  }
  to {
    opacity: 1;
    transform: translateX(0%) translateY(0%) rotateY(0deg);
  }
`;
const infoEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(30%);
  }
  to {
    opacity: 1;
    transform: translateY(0%);
  }
`;
const InnerWrapper = styled(Vertical).attrs({ gap: 3 })`
  --border-color: var(--c-score-high);
  --bg-rgb-color: 34, 252, 95;

  position: relative;
  aspect-ratio: 210 / 340;
  border: 4px solid var(--border-color);
  border-radius: var(--double-and-a-half-unit);
  padding-top: var(--double-unit);
  opacity: 0;
  overflow: hidden;
  height: 100%;

  animation: 0.5s ${cardEnter} ease-in-out var(--delay) forwards;

  background: radial-gradient(
      60% 27% at 50% 106.3%,
      rgba(var(--bg-rgb-color), 0.5) 0%,
      rgba(var(--bg-rgb-color), 0) 100%
    ),
    linear-gradient(180deg, transparent 20%, var(--c-black) 70%),
    var(--picture-url-bg),
    linear-gradient(
      180deg,
      rgba(var(--bg-rgb-color), 0.2) 0%,
      rgba(var(--bg-rgb-color), 0) 49.95%
    ),
    var(--c-black);
`;
const FullHeightVertical = styled(Vertical).attrs({ gap: 1 })`
  width: 100%;
  height: 100%;
  flex: 1;
  justify-content: space-between;
`;
const TopLeftIcon = styled.div`
  padding: 0 var(--double-unit);
  align-self: flex-start;
`;
const CenteredIconWrapper = styled(Horizontal).attrs({ center: true })`
  flex: 1;
`;
const SeasonalityLabelWrapper = styled.div`
  opacity: 0;
  animation: ${infoEnter} 0.4s ease-out calc(var(--delay) + 550ms) forwards;
`;
const Title = styled(BodyM)`
  text-align: center;
  opacity: 0;
  animation: ${infoEnter} 0.4s ease-out calc(var(--delay) + 500ms) forwards;
`;

const findAppearanceWithBestScore = (
  appearances?: CardLayout_so5lineup['so5Appearances']
): CardLayout_so5lineup['so5Appearances'][number] | undefined => {
  return appearances?.reduce(
    (acc, cur) =>
      (cur.so5Score?.score || 0) > (acc.so5Score?.score || 0) ? cur : acc,
    appearances[0]
  );
};

type Props = {
  delay: number;
  so5Lineup: CardLayout_so5lineup | null;
  topLeftIcon?: ReactNode;
  icon?: ReactNode;
  subtitle?: ReactNode;
  title: ReactNode;
  stats: ReactNode;
  footer: ReactNode;
  borderColor: Color;
  bgRgbColor: string;
};
export const CardLayout = ({
  delay,
  so5Lineup,
  topLeftIcon,
  icon,
  subtitle,
  title,
  stats,
  footer,
  borderColor,
  bgRgbColor,
  children,
}: PropsWithChildren<Props>) => {
  const bestAppearance = findAppearanceWithBestScore(so5Lineup?.so5Appearances);
  return (
    <InnerWrapper
      style={
        {
          '--border-color': borderColor,
          '--bg-rgb-color': bgRgbColor,
          '--picture-url-bg': bestAppearance
            ? `url(${bestAppearance?.player.pictureUrl}) no-repeat 0 30px / cover`
            : `linear-gradient(to bottom, transparent, transparent)`,
          '--delay': `${delay}ms`,
        } as CSSProperties
      }
    >
      <TopLeftIcon>{topLeftIcon}</TopLeftIcon>
      <CenteredIconWrapper>{icon}</CenteredIconWrapper>
      <FullHeightVertical>
        <Vertical gap={0} center>
          <SeasonalityLabelWrapper>
            <BodyS as="p">{subtitle}</BodyS>
          </SeasonalityLabelWrapper>
          <Title bold>{title}</Title>
          {children}
        </Vertical>
        {stats}
        {footer}
      </FullHeightVertical>
    </InnerWrapper>
  );
};

CardLayout.fragments = {
  so5Lineup: gql`
    fragment CardLayout_so5lineup on So5Lineup {
      id
      so5Appearances {
        id
        player {
          slug
          pictureUrl(derivative: "squared")
        }
        so5Score {
          id
          score
        }
      }
    }
  ` as TypedDocumentNode<CardLayout_so5lineup>,
};
